import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button,
  Typography, Alert, Container, IconButton, InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { studentPortalAPI } from '../../services/api';
import { useStudentAuth } from '../../hooks/useStudentAuth';

export default function StudentChangePassword() {
  const navigate = useNavigate();
  const { token, updateStudent, student } = useStudentAuth();

  const [form, setForm]       = useState({ current_password: '', password: '', password_confirmation: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [show, setShow]       = useState({ current: false, password: false, confirm: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirmation) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await studentPortalAPI.changePassword(form, token);
      updateStudent({ ...student, must_change_password: false });
      navigate('/student/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography variant="h6" fontWeight={600} mb={1}>Set New Password</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              You must change your password before continuing.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth margin="normal" type={show.current ? 'text' : 'password'} label="Current Password"
                value={form.current_password}
                onChange={(e) => setForm((p) => ({ ...p, current_password: e.target.value }))}
                required
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow((s) => ({ ...s, current: !s.current }))} edge="end">
                      {show.current ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}}
              />
              <TextField
                fullWidth margin="normal" type={show.password ? 'text' : 'password'} label="New Password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                required inputProps={{ minLength: 8 }}
                helperText="Minimum 8 characters"
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow((s) => ({ ...s, password: !s.password }))} edge="end">
                      {show.password ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}}
              />
              <TextField
                fullWidth margin="normal" type={show.confirm ? 'text' : 'password'} label="Confirm New Password"
                value={form.password_confirmation}
                onChange={(e) => setForm((p) => ({ ...p, password_confirmation: e.target.value }))}
                required
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow((s) => ({ ...s, confirm: !s.confirm }))} edge="end">
                      {show.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )}}
              />
              <Button type="submit" fullWidth variant="contained" size="large" disabled={loading} sx={{ mt: 3 }}>
                {loading ? 'Saving...' : 'Set Password & Continue'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
